import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { buildExpertReportItemPath, useExpertReportBlockRule } from '@/entities/expert-report';
import { buildKmEvalPath, useKmEval } from '@/entities/expert-report-km';
import { ExpertReportModelModeContext } from './expertReportModelModeContext';

/** То, чем задана оценка, вместе с местом, где оно живёт в устройстве своего заключения. */
export type ReportRule = {
  /** Как открытое называется в своём устройстве */
  title: string;
  /** Наименование; пусто, пока открытое читается или если его не нашлось */
  name: string;
  text: string;
  isLoading: boolean;
  /** Адрес самого открытого в разделе экспертных заключений; пусто — вести некуда */
  path?: string;
};

type Params = {
  /** Правило прежнего заключения либо определение оценки заключения на модели знаний */
  ruleId?: number;
  expertReportId: number;
  /** Блок результата, в котором стоит ссылка */
  blockId: number;
};

/**
 * Оценка по ссылке из результата заключения: у прежнего заключения — правило блока,
 * у заключения на модели знаний — определение оценки. Номера правил и определений идут
 * независимо и совпадают сплошь и рядом, поэтому ссылка читается ровно в том устройстве,
 * по которому посчитано заключение.
 */
export const useReportRule = ({ ruleId, expertReportId, blockId }: Params): ReportRule => {
  const { t } = useTranslation(['dialogs']);
  const modelMode = useContext(ExpertReportModelModeContext);
  const isKmMode = modelMode === 'km';

  const legacyRule = useExpertReportBlockRule(isKmMode ? undefined : ruleId);
  const kmEval = useKmEval(isKmMode ? ruleId : undefined);

  if (isKmMode) {
    // Единица оценивания берётся у самого определения: комментарий может сослаться и на
    // определение чужой единицы, а его собственная известна только ему
    const { id: evalId, assessmentId } = kmEval.data ?? {};
    return {
      title: t('expertReport.evalTitle'),
      name: kmEval.data?.name ?? '',
      text: kmEval.data?.text ?? '',
      isLoading: kmEval.isLoading,
      path: evalId && assessmentId ? buildKmEvalPath(expertReportId, assessmentId, evalId) : undefined,
    };
  }

  return {
    title: t('expertReport.ruleTitle'),
    name: legacyRule.data?.name ?? '',
    text: legacyRule.data?.text ?? '',
    isLoading: legacyRule.isLoading,
    path: buildExpertReportItemPath(expertReportId, 'block', blockId),
  };
};
